// Play / Score detail, Apogee: opened by tapping the score, like the
// Puzzle's goal. The words spelled so far with their points, any points the
// alignments add to the play, the final percentage, and the chosen
// alignments in the order chosen, each with what it adds right now or
// "inactive" while its planet is free (docs/02-Game-Design/Alignments.md).
// Tapping it closes it. It starts closed.
import { scorePlay, type PlayLevel, type PlayState } from '@wits/core';
import { PlanetIcon } from '../sky/PlanetIcon';

interface Props {
  level: PlayLevel;
  board: PlayState;
  onClose: () => void;
}

export function ScoreDetail({ level, board, onClose }: Props) {
  const score = scorePlay(level, board);
  const extras = score.playAdditions.length > 0 || score.finalPercent > 0;
  return (
    <div className="panel score-detail" onClick={onClose}>
      {score.words.length === 0 ? (
        <p className="score-detail-empty quiet">Spell a word to see its score here</p>
      ) : (
        <table className="score-detail-table">
          <tbody>
            {score.words.map((w) => (
              <tr key={w.word}>
                <td className="score-detail-word mono">{w.word}</td>
                <td className="score-detail-points mono">{w.total}</td>
              </tr>
            ))}
            {score.playAdditions.map((a) => (
              <tr key={a.alignment}>
                <td className="score-detail-word">
                  {a.label} <span className="mono">× {a.count}</span>
                </td>
                <td className="score-detail-points mono">{a.total}</td>
              </tr>
            ))}
            {score.finalPercent > 0 && (
              <tr>
                <td className="score-detail-word">{score.alignments.find((a) => a.active && a.alignment === 'surety')?.name ?? 'Final'}</td>
                <td className="score-detail-points mono">+{score.finalPercent}%</td>
              </tr>
            )}
            {extras && (
              <tr className="is-total">
                <td className="score-detail-word">So far</td>
                <td className="score-detail-points mono">{score.running}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {score.alignments.length > 0 && (
        <div className="score-detail-alignments">
          <div className="score-detail-label">Alignments</div>
          {score.alignments.map((a) => (
            <div key={a.planet.id} className={a.active ? 'score-detail-alignment' : 'score-detail-alignment is-inactive'}>
              <PlanetIcon level={level} planet={a.planet} size={22} state={a.active ? 'active' : 'inactive'} />
              <div>
                <div className="score-detail-alignment-name">{a.name}</div>
                <div className="score-detail-alignment-rule">{a.text}</div>
              </div>
              <div className="score-detail-alignment-effect">{a.active ? `+${a.points}` : 'inactive'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
